// Initial form values, ported from v1 so create, edit and drag-created open the same way.

import dayjs from 'dayjs'

import type { IOption } from 'common/interfaces/IOption'
import { getCurrentTimeInTimezone, toSiteWallClockDate } from 'common/services/dateService'
import {
  EHR_INITIAL_PATIENT_DATA,
  NEW_PATIENT_FORM_KEYS,
} from 'features/Home/constants/infoNewPatientFormKeys'
import type { IPatientConsultationStaff, IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule, PatientStatus } from 'features/Home/interfaces/IInfoSchedule'
import type { EHRAppointmentFormType } from 'features/Home/schemas/infoNewPatientSchema'
import type { AppointmentCategories } from 'features/Schedule/api/schedule.types'

import { appointmentProviders } from './appointmentProviders'
import type { ManageEhrAppointmentV2Props } from './ehrAppointmentV2.types'
import { defaultCreateRoom } from './futureRoom'

type Args = Pick<
  ManageEhrAppointmentV2Props,
  | 'isEditing'
  | 'editedPatientData'
  | 'customAppointment'
  | 'appointmentCategories'
  | 'patientStatuses'
  | 'byPassTemperatureCheck'
  | 'initialCategoryName'
  | 'initialStatusCode'
  | 'scheduleView'
  | 'timeZone'
  | 'date'
  | 'hasEhrBooking'
> & {
  rooms: IPatientRoom[]
  languages: IOption[]
  consultationStaffs: IPatientConsultationStaff[]
}

const defaultCategory = (
  categories: AppointmentCategories[],
  preferredName?: string,
): AppointmentCategories | undefined => {
  const byName = preferredName
    ? categories.find((category) =>
        category.name.toLowerCase().includes(preferredName.toLowerCase()),
      )
    : undefined

  return byName ?? categories.find((category) => category.default === 1) ?? categories[0]
}

const defaultStatus = (
  statuses: PatientStatus[] | undefined,
  preferredCode?: number,
): PatientStatus | undefined => {
  const byCode =
    preferredCode !== undefined
      ? statuses?.find((status) => status.code === preferredCode)
      : undefined

  return byCode ?? statuses?.find((status) => status.default === 1) ?? statuses?.[0]
}

// Non-OE creates leave the status to the backend, as v1 did.
const createStatus = (
  hasEhrBooking: boolean,
  statuses: PatientStatus[] | undefined,
  preferredCode?: number,
) => (hasEhrBooking ? defaultStatus(statuses, preferredCode)?.code ?? '' : '')

const editValues = (edited: IPatientSchedule, timeZone: string) => ({
  [NEW_PATIENT_FORM_KEYS.ID]: edited?.id,
  // The API can send patient: null, so every patient read is optional.
  [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: edited?.patient?.first_name,
  [NEW_PATIENT_FORM_KEYS.LAST_NAME]: edited?.patient?.last_name,
  [NEW_PATIENT_FORM_KEYS.PHONE]: edited?.patient?.phone,
  [NEW_PATIENT_FORM_KEYS.LANGUAGE]: edited?.patient?.language_type_code,
  [NEW_PATIENT_FORM_KEYS.ROOM]:
    typeof edited?.room === 'string' ? edited?.room : edited?.room?.value,
  [NEW_PATIENT_FORM_KEYS.TIME]:
    edited?.start_time instanceof Date || !edited?.start_time
      ? toSiteWallClockDate(edited?.start_time, timeZone)
      : new Date(
          `${edited?.date ?? getCurrentTimeInTimezone(timeZone).format('YYYY-MM-DD')} ${
            edited?.start_time
          }`,
        ),
  [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: edited?.category?.code,
  [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: edited?.consultation_staff,
  [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: Boolean(edited?.temperature_bypass),
  [NEW_PATIENT_FORM_KEYS.TITLE]: edited?.title,
  [NEW_PATIENT_FORM_KEYS.DURATION]: dayjs(edited?.end_time).diff(
    dayjs(edited?.start_time),
    'minutes',
  ),
  [NEW_PATIENT_FORM_KEYS.DATE]: edited?.date,
  [NEW_PATIENT_FORM_KEYS.COMMENTS]: edited?.comments || '',
  [NEW_PATIENT_FORM_KEYS.PATIENT_STATUS]: edited?.status?.code ?? '',
  [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE]: edited?.punctuality?.appointment?.code,
  [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES]: edited?.punctuality?.appointment?.minutes,
  [NEW_PATIENT_FORM_KEYS.PATIENT_PUNCTUALITY]: {
    code: edited?.punctuality?.patient?.code,
    minutes: edited?.punctuality?.patient?.minutes,
  },
})

// The clicked cell's room ("Label/value"), used only by a site without a waiting room.
const clickedRoom = (cell: string | undefined, rooms: IPatientRoom[]): string => {
  const [name, code] = cell?.split('/') ?? []
  const room = rooms.find((item) => item.label === name && String(item.value) === code)
  return room ? String(room.value) : ''
}

export const getInitialFormValues = (args: Args): EHRAppointmentFormType => {
  const {
    isEditing,
    editedPatientData,
    customAppointment,
    appointmentCategories,
    patientStatuses,
    byPassTemperatureCheck,
    initialCategoryName,
    initialStatusCode,
    timeZone,
    date,
    rooms,
    languages,
    consultationStaffs,
    hasEhrBooking,
  } = args

  const defaultLanguageCode = languages.find((language: IOption) => language.code === 1)?.code
  const doctor = consultationStaffs.find((staff) => staff.is_doctor === 1)

  if (isEditing && editedPatientData) {
    return editValues(editedPatientData, timeZone) as unknown as EHRAppointmentFormType
  }

  if (customAppointment) {
    // Previous logic, kept for a possible return: Day view seeded the clicked cell's room.
    // const [name, code] = customAppointment.room.split('/')
    // const room =
    //   scheduleView === EScheduleView.DAY
    //     ? rooms.find((item) => item.label === name && item.value === code)
    //     : rooms.find((item) => item.is_waiting_room)

    const category = defaultCategory(appointmentCategories)
    const staff =
      (customAppointment.consultationStaffId != null
        ? appointmentProviders(consultationStaffs).find(
            (item) => item.id === customAppointment.consultationStaffId,
          )
        : undefined) ?? doctor

    return {
      ...EHR_INITIAL_PATIENT_DATA,
      [NEW_PATIENT_FORM_KEYS.APPOINTMENT]: customAppointment?.appointment,
      [NEW_PATIENT_FORM_KEYS.ROOM]: defaultCreateRoom(
        rooms,
        clickedRoom(customAppointment.room, rooms),
      ),
      [NEW_PATIENT_FORM_KEYS.LANGUAGE]: defaultLanguageCode,
      [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: staff?.id,
      [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: category?.code || '',
      [NEW_PATIENT_FORM_KEYS.TITLE]: category?.name || '',
      [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: byPassTemperatureCheck,
      [NEW_PATIENT_FORM_KEYS.DURATION]: category?.duration ?? 5,
      [NEW_PATIENT_FORM_KEYS.DATE]: customAppointment?.appointment?.[0],
      [NEW_PATIENT_FORM_KEYS.PATIENT_STATUS]: createStatus(hasEhrBooking, patientStatuses),
      [NEW_PATIENT_FORM_KEYS.TIME]: new Date(customAppointment?.appointment?.[0]),
    } as unknown as EHRAppointmentFormType
  }

  const category = defaultCategory(appointmentCategories, initialCategoryName)
  const day = editedPatientData?.date || date

  return {
    ...EHR_INITIAL_PATIENT_DATA,
    // Previous logic, kept for a possible return: the waiting room only for a future site day.
    // [NEW_PATIENT_FORM_KEYS.ROOM]: roomForDay(EHR_INITIAL_PATIENT_DATA.room, day, timeZone, rooms),
    [NEW_PATIENT_FORM_KEYS.ROOM]: defaultCreateRoom(rooms),
    [NEW_PATIENT_FORM_KEYS.LANGUAGE]: defaultLanguageCode,
    [NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF]: doctor?.id,
    [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: category?.code || '',
    [NEW_PATIENT_FORM_KEYS.TITLE]: category?.name || '',
    [NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS]: byPassTemperatureCheck,
    [NEW_PATIENT_FORM_KEYS.TIME]: toSiteWallClockDate(new Date(), timeZone),
    [NEW_PATIENT_FORM_KEYS.DURATION]: category?.duration ?? 5,
    [NEW_PATIENT_FORM_KEYS.DATE]: day,
    [NEW_PATIENT_FORM_KEYS.PATIENT_STATUS]: createStatus(
      hasEhrBooking,
      patientStatuses,
      initialStatusCode,
    ),
  } as unknown as EHRAppointmentFormType
}
